<script lang="ts">
  import { showToast } from '../lib/toast';
  import { _ } from 'svelte-i18n';
  import PhoneInput from '../components/PhoneInput.svelte';
  import EmailInput from '../components/EmailInput.svelte';
  import ButtonSwitch from '../components/ButtonSwitch.svelte';
  import { verifyCode, verifyEmailCode } from '../lib/auth';
  import { signupStepOne, signupStepOneEmail, trackEvent, joinTeamViaInvitation, sendMagicLink as sendLoginMagicLink, sendEmailMagicLink } from '../lib/apiService';
  import { hashPhoneNumber } from '../lib/utils';
  import { onMount } from 'svelte';
  import { settingsStore } from '../stores/settings';
  import type { VerifyCodeRequest } from '../types';

  // Auth mode
  let authMode: 'email' | 'phone' = 'email';

  // Form data - Email mode
  let email = '';

  // Form data - Phone mode
  let countryCode = '+31';
  let phoneNumber = '';
  let messagingApp = 'whatsapp';

  // Common form data
  let invitationCode = '';
  let verificationCode = '';

  // UI state
  let currentStep = 1;
  let isLoading = false;
  let error = '';
  let showAppError = false;

  // Whether to show the resend option
  let showResendOption = false;
  let showEmailExistsOption = false;
  let resendLoading = false;

  // Access grant state
  let accessGrantToken = '';
  let showTeamJoinSuccess = false;
  let joinedTeamName = '';
  let joinedTeamId = '';

  // Load saved email from localStorage
  onMount(async () => {
    const savedEmail = localStorage.getItem('pankla_signup_email');
    if (savedEmail) {
      email = savedEmail;
    }

    // Parse token from URL path
    const path = window.location.hash.split('#')[1] || '';
    const pathParts = path.split('/');

    // Check if URL is /signup/{token}
    if (pathParts.length >= 3 && pathParts[1] === 'signup' && pathParts[2]) {
      accessGrantToken = pathParts[2];

      // Option 1: Check if user is already logged in
      if ($settingsStore?.user?.id) {
        console.log('user logged in');
        await handleLoggedInUserInvitation();
        return;
      }
    }
  });

  // Handle Option 1: User is already logged in
  async function handleLoggedInUserInvitation() {
    try {
      await joinTeamViaInvitation(accessGrantToken, (result, status) => {
        if (status === 200) {
          showTeamJoinSuccess = true;
          joinedTeamName = result.team_name;
          joinedTeamId = result.team_id;
        } else {
          showToast($_('signup.team_join.error'), 'error');
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        }
      });
    } catch (error) {
      console.error('Error joining team:', error);
      showToast('Failed to join team', 'error');
    }
  }

  // Handle registered user with invitation (email mode)
  async function handleRegisteredUserWithInvitationEmail() {
    try {
      const currentUrl = window.location.href;

      await sendEmailMagicLink(email, currentUrl, (result, status) => {
        if (status === 200) {
          showToast($_('signup.login_link_sent_email'), 'success');
          showEmailExistsOption = false;
          currentStep = 4;
        } else {
          showToast($_('signup.error_sending_login_link'), 'error');
          showEmailExistsOption = true;
        }
      });
    } catch (error) {
      console.error('Error sending email magic link:', error);
      showToast($_('signup.error_sending_login_link'), 'error');
      showEmailExistsOption = true;
    }
  }

  // Handle registered user with invitation (phone mode)
  async function handleRegisteredUserWithInvitation() {
    try {
      const currentUrl = window.location.href;

      await sendLoginMagicLink(phoneNumber, countryCode, currentUrl, (result, status) => {
        if (status === 200) {
          showToast($_('signup.login_link_sent', { values: { app: result.messaging_app } }), 'success');
          showResendOption = false;
          currentStep = 4;
        } else {
          showToast($_('signup.error_sending_login_link'), 'error');
          showResendOption = true;
        }
      });
    } catch (error) {
      console.error('Error sending magic link:', error);
      showToast($_('signup.error_sending_login_link'), 'error');
      showResendOption = true;
    }
  }

  function goToStep(step) {
    if (step === 2 && !validateStep1()) {
      return;
    }
    currentStep = step;
  }

  function validateStep1() {
    if (authMode === 'email') {
      if (!email.trim()) {
        showToast($_('signup.error'), 'error');
        return false;
      }
      // Basic email validation
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(email)) {
        showToast($_('signup.invalid_email'), 'error');
        return false;
      }
    } else {
      if (!phoneNumber.trim()) {
        showToast($_('signup.error'), 'error');
        return false;
      }
    }
    return true;
  }

  // Switch between email and phone modes
  function switchToPhone() {
    authMode = 'phone';
    currentStep = 1;
    error = '';
    showResendOption = false;
    showEmailExistsOption = false;
    showAppError = false;
  }

  function switchToEmail() {
    authMode = 'email';
    currentStep = 1;
    error = '';
    showResendOption = false;
    showEmailExistsOption = false;
    showAppError = false;
  }

  // Step 1: Send magic link (email mode)
  async function sendEmailVerification() {
    isLoading = true;

    // Save email to localStorage
    localStorage.setItem('pankla_signup_email', email);

    try {
      await signupStepOneEmail({
        email: email,
        invitation_code: invitationCode,
        access_grant_token: accessGrantToken
      }, (result, status) => {
        isLoading = false;

        if (status === 200) {
          currentStep = 3;
          trackEvent('send_signup_code_email', {
            module: "signup",
            action_type: "account_changes",
            description: 'Send signup code via email',
            value: { invitation_code: invitationCode }
          }, $settingsStore.user?.id);

        } else if (status === 400 && result === 'Email already registered') {
          if (accessGrantToken) {
            handleRegisteredUserWithInvitationEmail();
          } else {
            showEmailExistsOption = true;
          }
        } else if (status === 429) {
          showToast(result, 'error');
        } else {
          showToast(`${$_('signup.error')}: ${result}`, 'error');
        }
      });
    } catch (error) {
      isLoading = false;
      showToast($_('signup.error'), 'error');
    }
  }

  // Step 1: Send magic link (phone mode)
  async function sendMagicLink() {
    isLoading = true;

    try {
      await signupStepOne({
        phone_number: phoneNumber,
        country_code: countryCode,
        messaging_app: messagingApp,
        invitation_code: invitationCode,
        access_grant_token: accessGrantToken
      }, (result, status) => {
        isLoading = false;

        if (status === 200) {
          currentStep = 3;
          hashPhoneNumber(countryCode + phoneNumber).then(hashedPhone => {
            trackEvent('send_signup_code', {
                module: "signup",
                action_type: "account_changes",
                description: 'Send signup code',
                value: {phone: 'hashedPhone', invitation_code: invitationCode}
            }, $settingsStore.user?.id);
          });

        } else if (status === 400 && result === 'Phone number already registered') {
          if (accessGrantToken) {
            handleRegisteredUserWithInvitation();
          } else {
            showResendOption = true;
          }
        } else if (status === 429) {
          showToast(result, 'error');
        } else if (status === 400 && result === 'messaging app error') {
          showAppError = true;
        } else {
          showToast(`${$_('signup.error')}: ${result}`, 'error');
        }
      });
    } catch (error) {
      isLoading = false;
      showToast($_('signup.error'), 'error');
    }
  }

  // Step 3: Verify code (email mode)
  async function handleVerifyEmailCode() {
    isLoading = true;
    error = '';

    try {
      const result = await verifyEmailCode({
        code: parseInt(verificationCode, 10),
        email: email,
        invitation_code: invitationCode || undefined
      });

      trackEvent('signup_code_verified_email', {
        module: "signup",
        action_type: "account_changes",
        description: 'Complete email signup process',
        value: { invitation_code: invitationCode }
      }, $settingsStore.user?.id);

      isLoading = false;

      if (accessGrantToken) {
        window.location.href = `/account/#/signup-complete?access_grant=${accessGrantToken}`;
      } else {
        window.location.href = '/account/#/signup-complete';
      }

    } catch (err) {
      isLoading = false;
      error = $_('signup.verification_error');
    }
  }

  // Step 3: Verify code (phone mode)
  async function handleVerifyCode() {
    isLoading = true;
    error = '';

    try {
      const payload: VerifyCodeRequest = {
        code: parseInt(verificationCode, 10),
        phone_number: phoneNumber,
        country_code: countryCode,
        ...(invitationCode && { invitation_code: invitationCode })
      };
      const result = await verifyCode(payload);

      hashPhoneNumber(countryCode + phoneNumber).then(hashedPhone => {
        trackEvent('signup_code_verified', {
            module: "signup",
            action_type: "account_changes",
            description: 'Complete signup process',
            value: {phone: 'hashedPhone', invitation_code: invitationCode}
        }, $settingsStore.user?.id);
      });

      isLoading = false;

      if (accessGrantToken) {
        window.location.href = `/account/#/signup-complete?access_grant=${accessGrantToken}`;
      } else {
        window.location.href = '/account/#/signup-complete';
      }

    } catch (err) {
      isLoading = false;
      error = $_('signup.verification_error');
    }
  }

  // Resend verification code
  async function resendCode() {
    resendLoading = true;
    showAppError = false;

    if (authMode === 'email') {
      try {
        await signupStepOneEmail({
          email: email,
          invitation_code: invitationCode,
          access_grant_token: accessGrantToken,
          resend: true
        }, (result, status) => {
          resendLoading = false;

          if (status === 200) {
            showToast($_('signup.resend_success'), 'success');
            showEmailExistsOption = false;
            currentStep = 3;
          } else if (status === 429) {
            showToast(result, 'error');
          } else {
            showToast(`${$_('signup.error')}: ${result}`, 'error');
          }
        });
      } catch (error) {
        resendLoading = false;
        showToast($_('signup.error'), 'error');
      }
    } else {
      try {
        await signupStepOne({
          phone_number: phoneNumber,
          country_code: countryCode,
          messaging_app: messagingApp,
          invitation_code: invitationCode,
          access_grant_token: accessGrantToken,
          resend: true
        }, (result, status) => {
          resendLoading = false;

          if (status === 200) {
            showToast($_('signup.resend_success'), 'success');
            showResendOption = false;
            currentStep = 3;
            hashPhoneNumber(countryCode + phoneNumber).then(hashedPhone => {
              trackEvent('resend_signup_code', {
                  module: "signup",
                  action_type: "account_changes",
                  description: 'Resend signup code',
                  value: hashedPhone
              }, $settingsStore.user?.id);
            });
          } else if (status === 429) {
            showToast(result, 'error');
          } else if (status === 400 && result === 'messaging app error') {
            showAppError = true;
          } else {
            showToast(`${$_('signup.error')}: ${result}`, 'error');
          }
        });
      } catch (error) {
        resendLoading = false;
        showToast($_('signup.error'), 'error');
      }
    }
  }

</script>

<div class="container">

  <header class="mt-6 mb-6">
    {#if !showTeamJoinSuccess && !showAppError}
      {#if currentStep === 1}
          <h1 class="mt-6 mb-6 brand-font">{$_('signup.title')}</h1>
          <p>{$_('signup.subtitle')}</p>
          <p>{$_('signup.subtitle_email')} <a href="https://imedu.notion.site/pankla-data-policy-h34f8a7647ytr362534g2" target="_blank">{$_('signup.privacy_text')}</a></p>
      {:else}
          <button
              type="button"
              class="secondary small"
              on:click={() => {showAppError = false; showResendOption = false; showEmailExistsOption = false; goToStep(currentStep - 1)}}
          >
              &lt; {$_('buttons.previous')}
          </button>
      {/if}
    {/if}
  </header>

  {#if showTeamJoinSuccess}
    <div class="card mt-6 mb-6">
      <h3>{$_('signup.team_join.title')}</h3>
      <p>{$_('signup.team_join.description', { values: { teamName: joinedTeamName } })}</p>
      <div class="flex flex-col space-y-4">
        <a href="/game-assist/" role="button" class="button primary">
          {$_('signup.team_join.go_to_game_assist')}
        </a>
      </div>
    </div>
  {:else if showAppError}
    <div class="card mt-6 mb-6">
      <p class="text text-lg">{$_('signup.app_error', { values: { app: messagingApp.charAt(0).toUpperCase() + messagingApp.slice(1) } })}</p>
      <p class="text text-sm">{$_('signup.problem_with_phone_number', { values: { app: messagingApp.charAt(0).toUpperCase() + messagingApp.slice(1) } })}</p>
      {#if messagingApp === 'signal'}
      <p class="text text-sm">
        <span class="text text-danger">
          <ion-icon name="information-circle-outline"></ion-icon>
        </span>
        {$_('signup.problem_with_phone_number2')}
      </p>
      {/if}
      <div class="flex flex-col space-y-4">
        <button
          type="button"
          class="primary mb-4"
          disabled={resendLoading}
          on:click={resendCode}
        >
          {resendLoading ? $_('signup.sending') : $_('signup.resend_code')}
        </button>
        <button
          type="button"
          class="secondary"
          on:click={() => {showAppError = false; showResendOption = false; goToStep(1)}}
        >
          {$_('signup.to_signup')}
        </button>
      </div>
    </div>
  {/if}

  {#if showResendOption}
    <div class="card mt-6 mb-6">
      <p class="text text-lg">{$_('signup.phone_number_exists')}</p>
      <div class="flex flex-col space-y-4">
        <a href="/account/#/login" role="button" class="button secondary">
          {$_('signup.go_to_login')}
        </a>
      </div>
    </div>
  {/if}

  {#if showEmailExistsOption}
    <div class="card mt-6 mb-6">
      <p class="text text-lg">{$_('signup.email_exists')}</p>
      <div class="flex flex-col space-y-4">
        <a href="/account/#/login" role="button" class="button secondary">
          {$_('signup.go_to_login')}
        </a>
      </div>
    </div>
  {/if}

  {#if !showResendOption && !showEmailExistsOption && !showAppError && !showTeamJoinSuccess}
    <form on:submit|preventDefault={currentStep === 1 ? () => goToStep(2) : currentStep === 2 ? (authMode === 'email' ? sendEmailVerification : sendMagicLink) : null} class="space-y-6">
      {#if currentStep === 1}
        <!-- Step 1: Contact Information -->
        {#if authMode === 'email'}
          <!-- Email Input -->
          <EmailInput
            bind:email
            label={$_('signup.email')}
            placeholder={$_('signup.email_placeholder')}
            required={true}
          />

          <!-- Invitation code -->
          {#if !accessGrantToken}
          <div class="mt-6 pt-4 mb-6">
            <label for="invitationCode">{$_('signup.invitation_code')}</label>
            <input
              id="invitationCode"
              type="text"
              bind:value={invitationCode}
              required
              placeholder={$_('signup.invitation_code_placeholder')}
            />
          </div>
          {/if}

          <!-- Next Button -->
          <div>
            <button type="submit">
              {$_('buttons.next')}
            </button>
          </div>

          <!-- Switch to phone -->
          <div class="text-center mt-4">
            <button type="button" class="link-button" on:click={switchToPhone}>
              {$_('signup.use_phone_instead')}
            </button>
          </div>
        {:else}
          <!-- Phone Input -->
          <PhoneInput
            bind:countryCode
            bind:phoneNumber
            label={$_('signup.phone_number')}
            placeholder={$_('signup.phone_number_placeholder')}
            required={true}
          />

          <!-- Contact Method -->
          <div class="mt-6 mb-6">
            <label for="messagingApp">{$_('signup.preferred_messaging_app')}</label>
            <ButtonSwitch
              bind:selected_value={messagingApp}
              options={[
                {value: 'whatsapp', label: $_('signup.whatsapp')}
              ]}
            />
          </div>

          <!-- Invitation code -->
          {#if !accessGrantToken}
          <div class="mt-6 pt-4 mb-6">
            <label for="invitationCode">{$_('signup.invitation_code')}</label>
            <input
              id="invitationCode"
              type="text"
              bind:value={invitationCode}
              required
              placeholder={$_('signup.invitation_code_placeholder')}
            />
          </div>
          {/if}

          <!-- Next Button -->
          <div>
            <button type="submit">
              {$_('buttons.next')}
            </button>
          </div>

          <!-- Switch to email -->
          <div class="text-center mt-4">
            <button type="button" class="link-button" on:click={switchToEmail}>
              {$_('signup.use_email_instead')}
            </button>
          </div>
        {/if}
      {:else if currentStep === 2}
        <!-- Step 2: Confirmation -->
        <div class="card mt-6 mb-6">
          {#if authMode === 'email'}
            <p class="text text-lg my-6">{$_('signup.confirm_email_title')}</p>
            <h3 class="my-6">{email}</h3>
          {:else}
            <p class="text text-lg my-6">{$_('signup.confirm_phone_title')}</p>
            <h3 class="my-6">{countryCode} {phoneNumber.replace(/^0/, '')}</h3>
          {/if}
          <button type="submit" class="primary mt-6" disabled={isLoading}>
            {isLoading ? $_('signup.sending') : $_('signup.confirm_button')}
          </button>
        </div>
      {:else if currentStep === 3}
        <!-- Step 3: Enter Verification Code -->
        <h1 class="mt-6 mb-6">{$_('signup.verify_code_title')}</h1>
        {#if authMode === 'email'}
          <p>{$_('signup.verify_code_subtitle_email', { values: { email: email } })}</p>
        {:else}
          <p>{$_('signup.verify_code_subtitle', { values: { app: messagingApp.charAt(0).toUpperCase() + messagingApp.slice(1) } })}</p>
        {/if}

        <form on:submit|preventDefault={authMode === 'email' ? handleVerifyEmailCode : handleVerifyCode}>
          <div class="form-group">
            <label for="verification-code">{$_('signup.verification_code_label')}</label>
            <input
              type="number"
              id="verification-code"
              bind:value={verificationCode}
              placeholder={$_('signup.verification_code_placeholder')}
              maxlength="6"
              pattern="[0-9]{6}"
              required
              class="form-control"
              autocomplete="one-time-code"
              data-1p-ignore
            />
          </div>

          {#if error}
            <div class="error-message text text-danger my-6" role="alert">
              {error}
            </div>
          {/if}

          <button
            type="submit"
            class="button-primary full-width"
            disabled={isLoading || !verificationCode || verificationCode.length < 4}
          >
            {#if isLoading}
              {$_('signup.verifying')}...
            {:else}
              {$_('signup.verify_code_button')}
            {/if}
          </button>
        </form>

        <article>
          <div class="flex gap-2">
            <ion-icon name="information-circle-outline"></ion-icon>
            {#if authMode === 'email'}
              <p class="text text-sm text-italic">{$_('signup.check_email_note')}</p>
            {:else}
              <p class="text text-sm text-italic">{$_('signup.problem_with_phone_number', { values: { app: messagingApp.charAt(0).toUpperCase() + messagingApp.slice(1) } })}</p>
            {/if}
          </div>
        </article>
      {:else if currentStep === 4}
        <!-- Step 4: Check your messages for login link -->
        <h1 class="mt-6 mb-6">{$_('signup.check_messages_title')}</h1>
        {#if authMode === 'email'}
          <p>{$_('signup.check_email_subtitle')}</p>
        {:else}
          <p>{$_('signup.check_messages_subtitle')}</p>
        {/if}
        <div class="card mt-6 mb-6">
          <p class="text text-sm text-bold">{$_('signup.login_link_explanation')}</p>
          <p class="text text-sm">{$_('signup.after_login_team_join')}</p>
        </div>
      {/if}
    </form>
  {/if}
</div>

<style>
  ion-icon {
    font-size: 3rem;
    color: var(--pico-primary);
  }

  .link-button {
    background: none;
    border: none;
    color: var(--pico-primary);
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    font-size: 0.875rem;
  }

  .link-button:hover {
    color: var(--pico-primary-hover);
  }
</style>
